import { useDebounce } from '@uidotdev/usehooks';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AvatarWithInitials } from '@eluve/blocks';
import {
  Badge,
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
  Text,
  Textarea,
  toast,
} from '@eluve/components';
import { TenantPatientPicker } from '@eluve/smart-blocks';

import {
  CLINICAL_TASK_CATEGORIES,
  CLINICAL_TASK_CATEGORY_ICONS,
  CLINICAL_TASK_CATEGORY_LABELS,
  CLINICAL_TASK_PRIORITIES,
  CLINICAL_TASK_PRIORITY_LABELS,
  CLINICAL_TASK_STATUSES,
  CLINICAL_TASK_STATUS_LABELS,
  type ClinicalTaskCategory,
  categoryIcon,
  categoryLabel,
} from './constants';
import type {
  ClinicalTaskEdit,
  PractitionerOption,
} from './useClinicalTasksPanelState';
import type { PatientSummary } from './usePatientSummary';

/** The card fields the edit sheet reads — satisfied by both a pending and an
 *  accepted task, so the one sheet edits either. */
export interface EditableTaskFields {
  readonly description: string;
  readonly category: string | null;
  readonly priority: string | null;
  readonly status: string;
  readonly dueDate: string | null;
  readonly ownerReference: string | null;
  readonly forDisplay: string | null;
}

// Autosave cadence — matches the note editor's DEBOUNCE_MS.
const DEBOUNCE_MS = 500;

/** UI-shaped field values ('' = unset where the edit payload uses null). */
type FieldValues = {
  description: string;
  category: string;
  priority: string;
  status: string;
  dueDate: string;
  ownerReference: string;
};

const toEdit = (v: FieldValues): ClinicalTaskEdit => ({
  description: v.description.trim(),
  category: v.category,
  priority: v.priority,
  status: v.status,
  dueDate: v.dueDate || null,
  ownerReference: v.ownerReference || null,
});

const sameEdit = (a: ClinicalTaskEdit, b: ClinicalTaskEdit): boolean =>
  a.description === b.description &&
  a.category === b.category &&
  a.priority === b.priority &&
  a.status === b.status &&
  a.dueDate === b.dueDate &&
  a.ownerReference === b.ownerReference;

/** One "Label | control" row in the DETAILS section. */
const FieldRow: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-start gap-3">
    <Text size="sm" color="tertiary" className="w-28 shrink-0 pt-2">
      {label}
    </Text>
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

/** Uppercase section heading (SUMMARY / DETAILS). */
const SectionLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <Text
    size="xs"
    weight="semibold"
    color="tertiary"
    className="tracking-wide uppercase"
  >
    {children}
  </Text>
);

/**
 * Searchable assignee picker — a Combobox over the tenant's Practitioners, each
 * shown with an initials avatar (color keyed off the reference, so trigger and
 * list agree). Filters client-side on display name.
 */
const AssigneeCombobox: FC<{
  practitioners: PractitionerOption[];
  value: string;
  onSelect: (reference: string) => void;
}> = ({ practitioners, value, onSelect }) => {
  const selected = practitioners.find((p) => p.reference === value);
  return (
    <Combobox>
      <ComboboxSelectButton
        className="w-full justify-between"
        variant="solid"
        aria-label="Assignee"
      >
        {selected ? (
          <span className="flex items-center gap-2">
            <AvatarWithInitials
              name={selected.display}
              id={selected.reference}
              size="sm"
            />
            {selected.display}
          </span>
        ) : (
          <span className="text-text-secondary">Pick an assignee</span>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown searchPlaceholder="Search assignee">
        <ComboboxEmpty>No matching assignee</ComboboxEmpty>
        {practitioners.map((p) => (
          <ComboboxOption
            key={p.reference}
            searchValue={p.display}
            onSelect={() => onSelect(p.reference)}
          >
            <ComboboxSelectCheck
              className="mr-2"
              selected={p.reference === value}
            />
            <span className="flex items-center gap-2">
              <AvatarWithInitials name={p.display} id={p.reference} size="sm" />
              {p.display}
            </span>
          </ComboboxOption>
        ))}
      </ComboboxDropdown>
    </Combobox>
  );
};

/**
 * The field body both sheets share (edit + create): Summary textarea and the
 * DETAILS rows. The two modes differ only in the fixed status text, the
 * patient row content, and how changes persist (`setField`'s `immediate` is
 * autosave in edit mode, a no-op in create mode).
 */
const TaskFieldsBody: FC<{
  values: FieldValues;
  practitioners: PractitionerOption[];
  /** Read-only status text — create mode only (required there). Unused when
   *  `editableStatus`, which renders the Status as a select instead. */
  statusLabel?: string;
  /** Edit mode: render the Status as an editable select over all 5 states. */
  editableStatus?: boolean;
  patientRow: ReactNode;
  descriptionPlaceholder?: string;
  setField: (patch: Partial<FieldValues>, immediate: boolean) => void;
  onDescriptionBlur?: () => void;
}> = ({
  values,
  practitioners,
  statusLabel,
  editableStatus,
  patientRow,
  descriptionPlaceholder,
  setField,
  onDescriptionBlur,
}) => (
  <>
    <div className="flex flex-col gap-2">
      <SectionLabel>Summary</SectionLabel>
      <Textarea
        aria-label="Summary"
        value={values.description}
        placeholder={descriptionPlaceholder}
        onChange={(e) =>
          setField({ description: e.currentTarget.value }, false)
        }
        onBlur={onDescriptionBlur}
        className="font-medium"
      />
    </div>

    <div className="flex flex-col gap-4">
      <SectionLabel>Details</SectionLabel>

      <FieldRow label="Type">
        <div
          role="radiogroup"
          aria-label="Type"
          className="flex flex-wrap gap-2"
        >
          {CLINICAL_TASK_CATEGORIES.map((c) => {
            const selected = c === values.category;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setField({ category: c }, true)}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  selected
                    ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary text-text-indigo-subtle'
                    : 'border-border-primary bg-white text-text-secondary hover:bg-bg-secondary'
                }`}
              >
                <Icon name={CLINICAL_TASK_CATEGORY_ICONS[c]} size="xs" />
                {CLINICAL_TASK_CATEGORY_LABELS[c as ClinicalTaskCategory]}
              </button>
            );
          })}
        </div>
      </FieldRow>

      <FieldRow label="Status">
        {editableStatus ? (
          // Edit mode: any of the five lifecycle states is settable directly.
          <Select
            value={values.status}
            onValueChange={(status) => setField({ status }, true)}
          >
            <SelectTrigger aria-label="Status">
              <SelectValue placeholder="Pick a status" />
            </SelectTrigger>
            <SelectContent>
              {CLINICAL_TASK_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {CLINICAL_TASK_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          // Create mode: read-only — a manually created task starts To do.
          <div className="rounded-md border border-border-primary bg-bg-secondary p-2.5">
            <Text size="sm" color="secondary">
              {statusLabel}
            </Text>
          </div>
        )}
      </FieldRow>

      <FieldRow label="Priority">
        <Select
          value={values.priority}
          onValueChange={(priority) => setField({ priority }, true)}
        >
          <SelectTrigger aria-label="Priority">
            <SelectValue placeholder="Pick a priority" />
          </SelectTrigger>
          <SelectContent>
            {CLINICAL_TASK_PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                {CLINICAL_TASK_PRIORITY_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="For (patient)">{patientRow}</FieldRow>

      <FieldRow label="Assigned to">
        <AssigneeCombobox
          practitioners={practitioners}
          value={values.ownerReference}
          onSelect={(ownerReference) => setField({ ownerReference }, true)}
        />
      </FieldRow>

      <FieldRow label="Planned date">
        <Input
          type="date"
          aria-label="Planned date"
          value={values.dueDate}
          onChange={(e) => setField({ dueDate: e.target.value }, true)}
        />
      </FieldRow>
    </div>
  </>
);

/**
 * Edit detail view for one Clinical Task — a right-side slide-over (same Sheet
 * motion as the findings review surface). Serves both a pending draft and an
 * accepted task; the caller supplies `onSave` (the update route for pending,
 * a browser-direct write for accepted) plus the status label. Edits AUTOSAVE
 * like the rest of Eluve's forms: chips/selects/date persist on change, the
 * summary after a typing pause (or blur). A failed save toasts and reverts the
 * field to the last stored value. Closing (X / overlay / Esc) keeps everything
 * saved. The review footer (Approve + Delete) renders only when `onAccept` /
 * `onDelete` are supplied (pending); an accepted edit shows a plain Done close.
 * Status is always an editable select here (all five states), so there is no
 * read-only status label — that's the create sheet's mode.
 * Reassignment changes only the owner — the creator keeps visibility via the
 * requester policy rule.
 */
export const EditClinicalTaskSheet: FC<{
  task: EditableTaskFields;
  practitioners: PractitionerOption[];
  onSave: (edit: ClinicalTaskEdit) => Promise<boolean>;
  /** Pending only — flush-then-accept. Absent on an accepted-task edit. */
  onAccept?: () => Promise<boolean>;
  /** Pending only — reject the draft. Absent on an accepted-task edit. */
  onDelete?: () => Promise<boolean>;
  onClose: () => void;
}> = ({ task, practitioners, onSave, onAccept, onDelete, onClose }) => {
  const [values, setValues] = useState<FieldValues>(() => ({
    description: task.description,
    category: task.category ?? 'general',
    priority: task.priority ?? 'routine',
    status: task.status,
    dueDate: task.dueDate ?? '',
    ownerReference: task.ownerReference ?? '',
  }));
  // Same values, readable inside the async save chain without stale closures.
  const valuesRef = useRef(values);
  const lastSavedRef = useRef<ClinicalTaskEdit>(toEdit(values));
  // Saves run strictly one at a time, in order — a select change while the
  // summary save is in flight queues behind it.
  const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const [busy, setBusy] = useState(false);

  /** Persist the current values if they differ from the last stored state. */
  const queueSave = useCallback((): Promise<boolean> => {
    saveChainRef.current = saveChainRef.current.then(async () => {
      const v = valuesRef.current;
      // Summary is required — an empty draft never autosaves; the field keeps
      // the user's in-progress state until it's valid again.
      if (!v.description.trim()) return true;
      const edit = toEdit(v);
      if (sameEdit(edit, lastSavedRef.current)) return true;
      if (await onSave(edit)) {
        lastSavedRef.current = edit;
        return true;
      }
      // Toast + revert — the sheet always reflects what is actually stored.
      const s = lastSavedRef.current;
      const reverted: FieldValues = {
        description: s.description,
        category: s.category,
        priority: s.priority ?? 'routine',
        status: s.status,
        dueDate: s.dueDate ?? '',
        ownerReference: s.ownerReference ?? '',
      };
      valuesRef.current = reverted;
      setValues(reverted);
      toast.error('Could not save the change.');
      return false;
    });
    return saveChainRef.current;
  }, [onSave]);

  /** Update fields; `immediate` persists now (chips/selects/date) — the
   *  summary instead rides the debounce below. */
  const setField = (patch: Partial<FieldValues>, immediate: boolean) => {
    const next = { ...valuesRef.current, ...patch };
    valuesRef.current = next;
    setValues(next);
    if (immediate) void queueSave();
  };

  // The summary autosaves once typing pauses (and on blur, below).
  const debouncedDescription = useDebounce(values.description, DEBOUNCE_MS);
  useEffect(() => {
    if (
      debouncedDescription.trim() &&
      debouncedDescription.trim() !== lastSavedRef.current.description
    ) {
      void queueSave();
    }
  }, [debouncedDescription, queueSave]);

  const accept = async () => {
    if (!onAccept) return;
    if (!valuesRef.current.description.trim()) {
      toast.error('A description is required.');
      return;
    }
    setBusy(true);
    try {
      // Flush anything not yet saved (e.g. summary inside the debounce
      // window); a failed save blocks the accept.
      if (!(await queueSave())) return;
      if (await onAccept()) {
        toast.success('Task approved.');
        onClose();
      } else {
        toast.error('Could not accept the task.');
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onDelete) return;
    setBusy(true);
    try {
      const rejected = await onDelete();
      if (rejected) {
        toast.success('Task removed.');
        onClose();
      } else {
        toast.error('Could not remove the task.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-[480px] flex-col gap-0 sm:max-w-[480px]"
        data-testid="clinical-task-edit-sheet"
      >
        <SheetTitle className="sr-only">Edit task</SheetTitle>

        <div className="border-b border-border-primary p-4">
          <Badge
            variant="secondary"
            className="gap-1 bg-bg-indigo-subtle-primary text-text-indigo-subtle"
          >
            <Icon name={categoryIcon(values.category)} size="xs" />
            {categoryLabel(values.category)}
          </Badge>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
          <TaskFieldsBody
            values={values}
            practitioners={practitioners}
            editableStatus
            patientRow={
              // Read-only: the task's patient is bound to the appointment.
              <div className="flex items-center gap-2 rounded-md border border-border-primary p-2.5">
                <Icon name="User" size="sm" />
                <Text size="sm" weight="medium">
                  {task.forDisplay ?? '—'}
                </Text>
              </div>
            }
            setField={setField}
            onDescriptionBlur={() => void queueSave()}
          />
        </div>

        <div className="flex gap-2 border-t border-border-primary p-4">
          {onDelete ? (
            <Button variant="destructive" disabled={busy} onClick={remove}>
              <Icon name="Trash2" size="sm" />
              Delete
            </Button>
          ) : null}
          {onAccept ? (
            <Button className="flex-1" disabled={busy} onClick={accept}>
              {busy ? 'Approving…' : 'Approve'}
            </Button>
          ) : (
            // Accepted-task edit: no review step — autosave already persisted
            // every change, so the footer just closes.
            <Button
              variant="outline"
              className="flex-1"
              disabled={busy}
              onClick={onClose}
            >
              Done
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Create view for a manually added Clinical Task (ENG-5243) — the same
 * slide-over as the edit sheet, in submit mode: nothing persists until
 * "Create task". The task is pre-scoped to the session's encounter + patient
 * (both read-only here); the source chip ("From encounter · <session>") is
 * supplied by the caller. The created task is born accepted (To do).
 */
export const CreateClinicalTaskSheet: FC<{
  /** Chip prefix, e.g. "From encounter" — supplied by the calling surface. */
  sourceLabel: string;
  /** The session's display name, or null to show the prefix alone. */
  sessionTitle: string | null;
  /** Best-effort patient summary for the read-only row (null = unknown). */
  patient: PatientSummary | null;
  practitioners: PractitionerOption[];
  /** Preselected assignee — the current clinician when resolvable. */
  defaultOwnerReference: string | null;
  /** Standalone (no-encounter) create from the Tasks view: render a searchable
   *  tenant patient picker instead of the read-only session patient, and
   *  require a selection before Create. Absent for encounter-scoped creates. */
  patientSelect?: { tenantId: string };
  onCreate: (input: ClinicalTaskEdit, patientId?: string) => Promise<boolean>;
  onClose: () => void;
}> = ({
  sourceLabel,
  sessionTitle,
  patient,
  practitioners,
  defaultOwnerReference,
  patientSelect,
  onCreate,
  onClose,
}) => {
  const [values, setValues] = useState<FieldValues>(() => ({
    description: '',
    category: 'general',
    priority: 'routine',
    // A manually created task starts accepted (To do); read-only in create.
    status: 'requested',
    dueDate: '',
    ownerReference: defaultOwnerReference ?? '',
  }));
  const [submitting, setSubmitting] = useState(false);
  // Standalone create only: the chosen tenant patient. Required before Create.
  const [selectedPatientId, setSelectedPatientId] = useState<string>();
  const patientRequired = Boolean(patientSelect);
  const canCreate =
    Boolean(values.description.trim()) &&
    (!patientRequired || Boolean(selectedPatientId));

  const create = async () => {
    if (!values.description.trim()) {
      toast.error('A summary is required.');
      return;
    }
    if (patientRequired && !selectedPatientId) {
      toast.error('A patient is required.');
      return;
    }
    setSubmitting(true);
    try {
      if (await onCreate(toEdit(values), selectedPatientId)) {
        toast.success('Task created.');
        onClose();
      } else {
        toast.error('Could not create the task.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex w-[480px] flex-col gap-0 sm:max-w-[480px]"
        data-testid="clinical-task-create-sheet"
      >
        <SheetTitle className="sr-only">Create task</SheetTitle>

        <div className="flex items-center gap-2 border-b border-border-primary p-4">
          <Text size="md" weight="semibold">
            Create task
          </Text>
          <Badge variant="secondary" className="font-medium">
            {sessionTitle ? `${sourceLabel} · ${sessionTitle}` : sourceLabel}
          </Badge>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
          <TaskFieldsBody
            values={values}
            practitioners={practitioners}
            statusLabel="To do"
            descriptionPlaceholder="e.g. Order fasting lipid panel"
            patientRow={
              patientSelect ? (
                // Standalone (Tasks view): the task belongs to no encounter, so
                // the clinician picks any tenant patient.
                <TenantPatientPicker
                  tenantId={patientSelect.tenantId}
                  selectedPatientId={selectedPatientId}
                  onPatientSelected={setSelectedPatientId}
                />
              ) : (
                // Read-only: the patient comes from the session and can't be
                // changed here — rendered without any editable affordance.
                <div className="flex items-center gap-2 rounded-md border border-border-primary p-2.5">
                  <Icon name="User" size="sm" />
                  <div className="flex min-w-0 flex-col">
                    <Text size="sm" weight="medium">
                      {patient?.display ?? '—'}
                    </Text>
                    {patient?.detail ? (
                      <Text size="xs" color="tertiary">
                        {patient.detail}
                      </Text>
                    ) : null}
                  </div>
                </div>
              )
            }
            setField={(patch) => setValues((v) => ({ ...v, ...patch }))}
          />
        </div>

        <div className="flex gap-2 border-t border-border-primary p-4">
          <Button variant="outline" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            disabled={submitting || !canCreate}
            onClick={create}
            data-testid="clinical-task-create-submit"
          >
            {submitting ? 'Creating…' : 'Create task'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
